import { Injectable, Injector } from "@angular/core";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { BaseService } from "@common/service/base.service";
import { UserService } from "@common/user/user.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { WorkflowRunDialogComponent } from "@org-common/lib/workflow/workflow-run-dialog/workflow-run-dialog.component";
import { addTeamWorkflow, addTeamWorkflowWithOrganisationConfigurePermission, IAddTeamWorkflowRunData, initialiseAddTeamWorkflowRunData } from "app/features/people/teams/add-team-workflow/add-team-workflow";
import { lastValueFrom } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class TeamsUiService extends BaseService {
    public constructor(
        injector: Injector,
        private commonDialogService: AdaptCommonDialogService,
        private teamsService: CommonTeamsService,
        private userService: UserService,
        private authorisationService: AuthorisationService,
    ) {
        super(injector);
    }

    public async promiseToCreateTeam() {
        const team: Team | undefined = await this.teamsService.promiseToCreateTeam();

        try {
            const person = (await this.userService.getCurrentPerson())!;

            // dont add the current person to the team leader role (i.e. if an ADAPT person creates a team in an organisation, they do not have a connection)
            if (person.getLatestConnection()) {
                this.log.info("Setting current person as team leader", person);
                team.teamLeaderPerson = person;
            } else {
                this.log.info("Current person has no connection in organisation - leaving blank", person);
            }

            const canConfigureOrganisation = await this.authorisationService.promiseToGetHasAccess(ConfigurationAuthService.ConfigureOrganisation);
            const runData = initialiseAddTeamWorkflowRunData({ team });
            await lastValueFrom(this.commonDialogService.open(WorkflowRunDialogComponent<IAddTeamWorkflowRunData>, {
                workflow: canConfigureOrganisation
                    ? addTeamWorkflowWithOrganisationConfigurePermission
                    : addTeamWorkflow,
                runData,
            }), { defaultValue: undefined });

            // if the temporary team was not overwritten, then the workflow was cancelled
            if (runData.team === team) {
                await lastValueFrom(this.commonDataService.rejectChanges([...runData.entitiesToConfirm, runData.team, team]));
                return undefined;
            }

            if (!team.entityAspect.entityState.isDetached()) {
                this.log.error("Temporary team not detached after creating team");
            }

            if (runData.team) {
                if (!runData.team.teamLeaderPersonId) {
                    this.log.error("Team should have a team leader after creation!", team);
                }

                const teamLeaderRoleConnections = runData.team.roleConnections.filter((rc: RoleConnection) => rc.connection.personId === team!.teamLeaderPersonId);
                if (teamLeaderRoleConnections.length !== 1) {
                    this.log.error("There should be exactly 1 team leader role connection after creation", teamLeaderRoleConnections);
                }

                return runData.team;
            }
        } catch (e) {
            await lastValueFrom(this.commonDataService.cancel());
            return Promise.reject(e);
        }
    }
}
